import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import type { PropsWithChildren } from 'react';

import Logo from 'reservations/account/components/Logo';
import { useAccount } from 'reservations/account/providers/AccountProvider';
import Header from 'reservations/base/components/Header';
import SystemError from 'reservations/base/components/SystemError';
import { formatPhone } from 'reservations/base/helpers/phone';

function ErrorScreenLayout({
  children,
  errorContent = '',
  errorTitle = '',
  hasImage = false
}: PropsWithChildren<{
  errorContent?: string;
  errorTitle?: string;
  hasImage?: boolean;
}>) {
  return (
    <Box>
      <Box
        sx={theme => ({
          opacity: '0.05',
          position: 'relative',
          minHeight: hasImage ? theme.spacing(20) : theme.spacing(10),
          backgroundColor: theme.palette.primary.main,
          mb: hasImage ? '0' : '128px'
        })}
      />
      {children}
      <Box sx={{ px: '32px' }}>
        <SystemError errorContent={errorContent} errorTitle={errorTitle} />
      </Box>
    </Box>
  );
}

export function ErrorScreenWithAccountDetails() {
  const { hasImage, logo, restaurantName } = useAccount();

  return (
    <ErrorScreenLayout hasImage={hasImage}>
      <Box
        sx={theme => ({
          textAlign: 'center',
          position: 'relative',
          bottom: hasImage ? theme.spacing(14) : 0,
          mb: hasImage ? '-80px' : 0
        })}
      >
        <Box sx={{ mb: '24px' }}>
          {hasImage && <Logo src={logo.url} alt={logo.alt_text} />}
        </Box>
        <Grid
          container
          alignContent="center"
          direction="column"
          rowSpacing="8px"
          sx={{
            mb: hasImage ? 0 : '20px'
          }}
        >
          <Grid item>
            <Header text={restaurantName} />
          </Grid>
        </Grid>
      </Box>
    </ErrorScreenLayout>
  );
}

type BookingsUnavailableErrorScreenProps = {
  phoneNumber: string;
};

export function BookingsUnavailableErrorScreen({
  phoneNumber
}: BookingsUnavailableErrorScreenProps) {
  const errorContent = phoneNumber
    ? `Please check back later, or give us a call at ${formatPhone(
        phoneNumber
      )}.`
    : 'Please check back later.';
  const errorTitle = 'Online bookings are currently unavailable';
  return (
    <ErrorScreenLayout errorContent={errorContent} errorTitle={errorTitle} />
  );
}

export function ErrorScreen() {
  return <ErrorScreenLayout />;
}
